<template>
  <div ref="mapContainer" class="z-0 size-full" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import * as L from 'leaflet';
import 'leaflet-imageoverlay-rotated';
import { getLayers, useLocalStorageLayer } from '@/utils/map/layer';
import { useMapResize } from '@/utils/map/resize';
import { mapCenter } from '@/config/constants';
import { createOverlayPane, renderMapOverlays } from '@/utils/map/overlay';

const props = defineProps({
  overlays: {
    type: Array,
    default: () => [],
  },
  focusOverlayId: {
    type: Number,
    default: null,
  },
});

const mapContainer = ref(null);
const map = ref(null);
const overlayLayerGroup = ref(L.layerGroup([]));
let cleanupResize = null;

const { id: savedLayerId } = useLocalStorageLayer();

onMounted(() => {
  const layers = getLayers();
  const initialLayer =
    savedLayerId === 'satellite' ? layers.satellite : layers.map;

  map.value = L.map(mapContainer.value, {
    center: mapCenter,
    zoom: 5,
    layers: [initialLayer],
  });

  L.control
    .layers({
      Map: layers.map,
      Satellite: layers.satellite,
    })
    .addTo(map.value);

  cleanupResize = useMapResize(map, mapContainer);

  createOverlayPane(map.value);
  overlayLayerGroup.value.addTo(map.value);
  renderOverlays();
  fitAllOverlays();
});

onBeforeUnmount(() => {
  if (cleanupResize) cleanupResize();
  if (map.value) {
    map.value.remove();
    map.value = null;
  }
});

function renderOverlays() {
  if (!map.value) return;
  renderMapOverlays(map.value, props.overlays, overlayLayerGroup.value);
}

function getOverlayBounds(overlay) {
  if (
    !overlay.corners?.topLeft ||
    !overlay.corners?.topRight ||
    !overlay.corners?.bottomLeft
  )
    return null;
  return L.latLngBounds([
    [overlay.corners.topLeft.lat, overlay.corners.topLeft.lng],
    [overlay.corners.topRight.lat, overlay.corners.topRight.lng],
    [overlay.corners.bottomLeft.lat, overlay.corners.bottomLeft.lng],
  ]);
}

function fitAllOverlays() {
  if (!map.value || !props.overlays?.length) return;

  const allBounds = props.overlays.map(getOverlayBounds).filter(Boolean);

  if (allBounds.length === 0) return;

  const combinedBounds = allBounds.reduce(
    (acc, bounds) => acc.extend(bounds),
    L.latLngBounds([]),
  );
  map.value.fitBounds(combinedBounds, { padding: [50, 50] });
}

function focusOverlay(id) {
  if (!map.value || !id) return;
  const overlay = props.overlays.find((o) => o.id === id);
  if (!overlay) return;
  const bounds = getOverlayBounds(overlay);
  if (bounds) {
    map.value.fitBounds(bounds, { padding: [50, 50] });
  }
}

watch(
  () => props.overlays,
  () => {
    renderOverlays();
    if (!props.focusOverlayId) {
      fitAllOverlays();
    }
  },
);

watch(
  () => props.focusOverlayId,
  (id) => {
    if (id) focusOverlay(id);
  },
);
</script>
